import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineExclamation, HiOutlineCheckCircle, HiOutlineBell, HiArrowRight, HiArrowLeft, HiOutlineShieldCheck, HiOutlineDatabase, HiOutlineMail } from 'react-icons/hi';
import { MdHistory } from 'react-icons/md';
import { deployReminderProtocol } from '../services/notificationService';

const FollowUp = () => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    redness: '', visionImproving: '', eyePain: '',
    otherSymptoms: [], customSymptom: '',
    flashes: '', eyeDrops: '',
    wantsReminder: null, contactInfo: ''
  });
  const [assessment, setAssessment] = useState(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentStep, setDeploymentStep] = useState(0);
  const deploymentMsg = [
    { icon: <HiOutlineShieldCheck />, text: 'Establishing Secure Clinical Link...' },
    { icon: <HiOutlineDatabase />, text: 'Syncing Recovery Trajectory...' },
    { icon: <HiOutlineMail />, text: 'Deploying Notification Protocol...' }
  ];

  const symptomOptions = ['Blurred vision', 'Excess tearing', 'Light sensitivity', 'Swelling', 'None'];

  const calculateAssessment = () => {
    const { redness, visionImproving, eyePain, flashes, eyeDrops } = answers;
    
    // 1. Immediate warning (High Priority Cluster)
    // If redness + pain + worsening vision
    if (redness !== 'No' && eyePain !== 'No' && (visionImproving === 'Worse' || visionImproving === 'No')) return 'IMMEDIATE_WARNING';
    
    // 2. Urgent / High Risk (Severe individual symptoms)
    if (eyePain === 'Severe' || visionImproving === 'Worse' || flashes === 'Frequent') return 'URGENT';
    
    // 3. Compliance Warning (Missed medication)
    if (eyeDrops === 'No' || eyeDrops === 'Sometimes Missed') return 'COMPLIANCE_WARNING';

    // 4. Warning / Clinical Alert (General concern)
    if (redness === 'Severe' || visionImproving === 'No') return 'WARNING';
    
    // 5. Normal Recovery
    // Vision improving + no pain
    if (visionImproving === 'Improved' && eyePain === 'No') return 'NORMAL';
    
    // 6. Mild Concern (Catch-all for minor anomalies)
    if (redness === 'Mild' || visionImproving === 'Slight' || eyePain === 'Mild' || flashes === 'Occasional') return 'MILD';
    
    return 'NORMAL';
  };

  const getAssessmentStyles = (status) => {
    switch(status) {
      case 'NORMAL': return { color: 'bg-green-500', bg: 'bg-green-500/10', text: 'text-green-500', label: 'NORMAL RECOVERY', sub: 'Your recovery appears to be progressing normally.', desc: 'Continue using prescribed medications and attend scheduled check-ups.' };
      case 'MILD': return { color: 'bg-yellow-500', bg: 'bg-yellow-500/10', text: 'text-yellow-500', label: 'MILD CONCERN', sub: 'Some symptoms indicate slower recovery than expected.', desc: 'It is recommended to monitor closely and follow up with your eye care provider.' };
      case 'COMPLIANCE_WARNING': return { color: 'bg-blue-500', bg: 'bg-blue-500/10', text: 'text-blue-500', label: 'MEDICATION ALERT', sub: 'Warning about medication compliance.', desc: 'Proper use of prescribed eye drops is critical for successful recovery. Please resume regular dosing immediately.' };
      case 'WARNING': return { color: 'bg-orange-600', bg: 'bg-orange-600/10', text: 'text-orange-600', label: 'WARNING (NEEDS CONSULTATION)', sub: 'Your symptoms suggest possible complications.', desc: 'Please consult an eye care professional as soon as possible.' };
      case 'IMMEDIATE_WARNING': return { color: 'bg-red-700', bg: 'bg-red-700/10', text: 'text-red-700', label: '🚨 IMMEDIATE WARNING', sub: 'Critical symptom cluster detected (Redness + Pain + Vision Loss).', desc: 'Stop this test and seek emergency eye care immediately.' };
      case 'URGENT': return { color: 'bg-red-600', bg: 'bg-red-600/10', text: 'text-red-600', label: 'URGENT (HIGH RISK)', sub: 'Severe symptoms detected (pain, worsening vision, flashes).', desc: 'Seek immediate medical attention at the nearest eye clinic or hospital.' };
      default: return {};
    }
  };

  const handleDeploy = async () => {
    setIsDeploying(true);
    const delay = assessment === 'URGENT' || assessment === 'WARNING' || assessment === 'IMMEDIATE_WARNING' ? 3 : 7;
    
    // Animation sequence
    for (let i = 0; i < deploymentMsg.length; i++) {
      setDeploymentStep(i);
      await new Promise(r => setTimeout(r, 1200));
    }

    try {
      await deployReminderProtocol({
        email: answers.contactInfo,
        delayDays: delay,
        assessment: assessment
      });
      setStep(9);
    } catch (error) {
      console.error(error);
      setStep(9); // Fallback to success page
    } finally {
      setIsDeploying(false);
    }
  };

  const nextStep = () => {
    if (step === 6) {
      setAssessment(calculateAssessment());
      setStep(7);
    } else {
      setStep(prev => prev + 1);
    }
  };

  return (
    <div className="pt-40 pb-24 px-4 min-h-screen bg-black mesh-gradient">
      <div className="max-w-3xl mx-auto relative z-10">
        {/* Luxury Header */}
        <div className="text-center mb-16 space-y-6">
          <div className="w-14 h-14 bg-[var(--gold-primary)] text-black rounded-xl flex items-center justify-center mx-auto shadow-[0_0_30px_var(--gold-glow)] rotate-3">
            <MdHistory size={30} />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">Follow <span className="text-gradient">up</span></h1>
          <h2 className="text-xl font-bold text-slate-300 uppercase tracking-widest">Post–Cataract Surgery Follow-Up System</h2>
          <div className="max-w-2xl mx-auto p-4 bg-white/5 border border-white/10 rounded-xl">
            <p className="text-[10px] text-orange-500 font-black uppercase tracking-widest mb-1">Disclaimer</p>
            <p className="text-xs text-slate-400 font-medium leading-relaxed italic">
              This is not a medical diagnosis. If you experience severe symptoms, seek immediate medical attention.
            </p>
          </div>
          <p className="text-slate-400 font-medium max-w-lg mx-auto italic">
            This system helps monitor your recovery after cataract surgery by asking a few simple questions about your eye condition.
          </p>
        </div>

        <AnimatePresence mode="wait">
          {step <= 6 && (
            <motion.div
              key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="gold-card p-8 md:p-12 overflow-hidden relative"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-[var(--gold-primary)]/5 blur-[80px] -mr-24 -mt-24" />
              <div className="mb-10 relative z-10">
                <span className="text-[10px] font-black text-[var(--gold-primary)] uppercase tracking-[0.3em] block mb-6">Phase 0{step} / 06</span>
                
                {step === 1 && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-black text-white leading-tight">1. Are you experiencing eye redness?</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {[
                        { id: 'No', l: ' No', d: 'Natural eye appearance' },
                        { id: 'Mild', l: ' Mild redness', d: 'Slight vascular visibility' },
                        { id: 'Severe', l: ' Severe redness', d: 'Deep crimson ocular profile' }
                      ].map(o => (
                        <button key={o.id} onClick={() => setAnswers({...answers, redness: o.id})} className={`p-6 rounded-2xl border transition-all text-left ${answers.redness === o.id ? 'border-[var(--gold-primary)] bg-[var(--gold-primary)]/10 shadow-[0_0_20px_var(--gold-glow)]' : 'border-white/5 bg-white/5 hover:border-white/10'}`}>
                          <span className="block font-black text-white uppercase tracking-tight">{o.l}</span>
                          <span className="block text-xs font-medium text-slate-500 mt-1 italic">{o.d}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-black text-white leading-tight">2. Is your vision improving after surgery?</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {[
                        { id: 'Improved', l: ' Yes, improving daily' },
                        { id: 'Slight', l: ' Slight improvement' },
                        { id: 'No', l: ' No improvement' },
                        { id: 'Worse', l: ' Getting worse' }
                      ].map(o => (
                        <button key={o.id} onClick={() => setAnswers({...answers, visionImproving: o.id})} className={`p-5 rounded-2xl border text-left font-black transition-all uppercase tracking-tight text-xs ${answers.visionImproving === o.id ? 'border-[var(--gold-primary)] bg-[var(--gold-primary)]/10 text-[var(--gold-primary)] shadow-[0_0_20px_var(--gold-glow)]' : 'border-white/5 bg-white/5 hover:border-white/10 text-slate-400'}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-black text-white leading-tight">3. Do you have eye pain?</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {[
                        { id: 'No', l: ' No pain' },
                        { id: 'Mild', l: ' Mild discomfort' },
                        { id: 'Severe', l: ' Severe pain' }
                      ].map(o => (
                        <button key={o.id} onClick={() => setAnswers({...answers, eyePain: o.id})} className={`p-5 rounded-2xl border text-left font-black transition-all uppercase tracking-tight text-xs ${answers.eyePain === o.id ? 'border-[var(--gold-primary)] bg-[var(--gold-primary)]/10 text-[var(--gold-primary)] shadow-[0_0_20px_var(--gold-glow)]' : 'border-white/5 bg-white/5 hover:border-white/10 text-slate-400'}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-black text-white leading-tight mb-2">4. Any of the following symptoms?</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {symptomOptions.map(s => (
                        <button key={s} onClick={() => {
                          setAnswers(prev => ({ ...prev, otherSymptoms: prev.otherSymptoms.includes(s) ? prev.otherSymptoms.filter(i => i !== s) : [...prev.otherSymptoms, s] }));
                        }} className={`p-4 rounded-2xl border text-left font-black transition-all uppercase tracking-tight text-xs ${answers.otherSymptoms.includes(s) ? 'border-[var(--gold-primary)] bg-[var(--gold-primary)]/10 text-[var(--gold-primary)] shadow-[0_0_20px_var(--gold-glow)]' : 'border-white/5 bg-white/5 hover:border-white/10 text-slate-400'}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                    <input 
                      type="text" placeholder="Type other symptoms..."
                      className="w-full p-5 bg-black/40 border-2 border-white/5 rounded-2xl focus:border-[var(--gold-primary)] outline-none transition-all font-black text-white placeholder:text-slate-700"
                      value={answers.customSymptom} onChange={(e) => setAnswers({...answers, customSymptom: e.target.value})}
                    />
                  </div>
                )}

                {step === 5 && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-black text-white">5. Do you see flashes or dark shadows?</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {[
                        { id: 'No', l: ' No' },
                        { id: 'Occasional', l: ' Occasionally' },
                        { id: 'Frequent', l: ' Yes, frequently' }
                      ].map(o => (
                        <button key={o.id} onClick={() => setAnswers({...answers, flashes: o.id})} className={`p-5 rounded-2xl border text-left font-black transition-all uppercase tracking-tight text-xs ${answers.flashes === o.id ? 'border-[var(--gold-primary)] bg-[var(--gold-primary)]/10 text-[var(--gold-primary)] shadow-[0_0_20px_var(--gold-glow)]' : 'border-white/5 bg-white/5 hover:border-white/10 text-slate-400'}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 6 && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-black text-white">6. Are you using prescribed eye drops properly?</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {[
                        { id: 'Yes', l: ' Yes regularly' },
                        { id: 'Sometimes Missed', l: ' Sometimes missed doses' },
                        { id: 'No', l: ' No' }
                      ].map(o => (
                        <button key={o.id} onClick={() => setAnswers({...answers, eyeDrops: o.id})} className={`p-5 rounded-2xl border text-left font-black transition-all uppercase tracking-tight text-xs ${answers.eyeDrops === o.id ? 'border-[var(--gold-primary)] bg-[var(--gold-primary)]/10 text-[var(--gold-primary)] shadow-[0_0_20px_var(--gold-glow)]' : 'border-white/5 bg-white/5 hover:border-white/10 text-slate-400'}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4 pt-10 border-t border-white/10 relative z-10">
                {step > 1 && (
                  <button onClick={() => setStep(step-1)} className="flex-1 p-5 bg-white/5 text-slate-400 rounded-2xl font-black hover:bg-white/10 transition-all flex items-center justify-center gap-2 tracking-[0.2em] text-[10px] uppercase">
                    <HiArrowLeft /> BACK
                  </button>
                )}
                <button 
                  onClick={nextStep}
                  className="flex-[2] p-5 bg-[var(--gold-primary)] text-black rounded-2xl font-black text-sm hover:bg-[var(--gold-light)] transition-all shadow-[0_0_20px_rgba(212,175,55,0.2)] flex items-center justify-center gap-3 group uppercase tracking-widest"
                >
                  {step === 6 ? 'ANALYZE DATA' : 'CONTINUE'} <HiArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          )}

          {step === 7 && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-2xl mx-auto">
              {(() => {
                const s = getAssessmentStyles(assessment);
                return (
                  <div className={`p-10 md:p-16 rounded-3xl bg-black border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] relative overflow-hidden`}>
                    <div className={`absolute top-0 right-0 w-64 h-64 ${s.bg} blur-[80px] -mr-32 -mt-32 opacity-20`} />
                    <div className="relative z-10 text-center">
                      <div className={`w-24 h-24 ${s.color} text-black rounded-3xl flex items-center justify-center mx-auto mb-10 shadow-[0_0_30px_var(--gold-glow)] rotate-6`}>
                        {assessment === 'NORMAL' ? <HiOutlineCheckCircle size={56} /> : <HiOutlineExclamation size={56} />}
                      </div>
                      <h2 className="text-[10px] font-black tracking-[0.4em] uppercase text-slate-500 mb-4">{s.label}</h2>
                      <h3 className="text-2xl md:text-3xl font-black text-white mb-6 tracking-tight leading-tight">{s.sub}</h3>
                      
                      <div className="p-8 bg-white/5 rounded-2xl border border-white/10 mb-12 text-left">
                        <p className="text-sm font-medium text-slate-400 leading-relaxed italic">
                          {s.desc}
                        </p>
                      </div>

                      <div className="flex flex-col gap-4 max-w-sm mx-auto">
                        <button onClick={() => setStep(8)} className="btn-primary py-5">
                          <HiOutlineBell size={20} /> ENABLE FOLLOW-UP REMINDER
                        </button>
                        <button onClick={() => { setStep(1); setAssessment(null); }} className="text-[10px] font-black text-slate-600 hover:text-[var(--gold-primary)] tracking-[0.3em] uppercase transition-colors">
                          RE-INITIALIZE DIAGNOSTICS
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          )}

          {step >= 8 && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="gold-card p-10 md:p-16 text-center">
              {step === 8 ? (
                <div className="max-w-sm mx-auto space-y-8">
                  <div className="w-20 h-20 bg-[var(--gold-primary)] text-black rounded-3xl flex items-center justify-center mx-auto shadow-[0_0_30px_var(--gold-glow)] -rotate-6">
                    <HiOutlineBell size={48} />
                  </div>
                  <h2 className="text-3xl font-black text-white tracking-tight">Recovery <span className="text-gradient">Reminder</span></h2>
                  <p className="text-slate-400 font-medium italic text-sm leading-relaxed">"Would you like to receive a follow-up reminder?"</p>
                  <div className="space-y-4">
                    <input 
                      type="text" placeholder="Enter phone/email"
                      className="w-full p-5 bg-black/40 border border-white/10 rounded-2xl focus:border-[var(--gold-primary)] outline-none text-center font-black text-white text-sm"
                      value={answers.contactInfo} onChange={(e) => setAnswers({...answers, contactInfo: e.target.value})}
                    />
                    <div className="flex gap-4">
                      <button 
                        onClick={handleDeploy} 
                        disabled={!answers.contactInfo || isDeploying}
                        className="flex-1 btn-primary py-4 text-xs uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isDeploying ? 'Deploying...' : 'Yes, Remind Me'}
                      </button>
                      <button onClick={() => setStep(10)} disabled={isDeploying} className="flex-1 p-5 bg-white/5 text-slate-400 rounded-2xl font-black hover:bg-white/10 transition-all uppercase text-[10px] tracking-widest disabled:opacity-50">No</button>
                    </div>
                  </div>
                  
                  {/* Deployment Animation Overlay */}
                  <AnimatePresence>
                    {isDeploying && (
                      <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/95 flex items-center justify-center z-[100] backdrop-blur-sm p-8"
                      >
                        <div className="text-center space-y-8 max-w-xs mx-auto">
                          <motion.div 
                            key={deploymentStep}
                            initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
                            className="w-20 h-20 bg-[var(--gold-primary)] text-black rounded-3xl mx-auto flex items-center justify-center shadow-[0_0_50px_var(--gold-glow)]"
                          >
                            <span className="text-4xl">{deploymentMsg[deploymentStep].icon}</span>
                          </motion.div>
                          <div className="space-y-2">
                            <h3 className="text-white font-black uppercase tracking-widest text-xs animate-pulse">
                              {deploymentMsg[deploymentStep].text}
                            </h3>
                            <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                              <motion.div 
                                initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 1.2 }}
                                className="h-full bg-[var(--gold-primary)]"
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : step === 9 ? (
                <div className="space-y-10 py-10">
                  <div className="text-7xl animate-bounce">📅</div>
                  <h2 className="text-4xl font-black text-white tracking-tighter">Reminder <span className="text-gradient">Scheduled</span></h2>
                  <p className="text-slate-400 font-medium max-w-sm mx-auto italic">
                    "A reminder email has been scheduled for {answers.contactInfo}. We will remind you in {assessment === 'URGENT' || assessment === 'WARNING' || assessment === 'IMMEDIATE_WARNING' ? '3 days' : '1 week'}" 
                    to check your recovery progress.
                  </p>
                  <button onClick={() => window.location.href = '/'} className="px-12 py-5 bg-[var(--gold-primary)] text-black rounded-2xl font-black tracking-widest hover:bg-[var(--gold-light)] transition-all shadow-[0_0_30px_var(--gold-glow)] active:scale-95 uppercase text-xs">
                    RETURN TO HOME
                  </button>
                </div>
              ) : (
                <div className="space-y-10 py-10">
                  <div className="text-7xl">✨</div>
                  <h2 className="text-4xl font-black text-white tracking-tighter">Diagnostic <span className="text-gradient">Complete</span></h2>
                  <p className="text-slate-400 font-medium max-w-sm mx-auto italic">Thank you for completed the post-surgery assessment.</p>
                  <button onClick={() => window.location.href = '/'} className="px-12 py-5 bg-[var(--gold-primary)] text-black rounded-2xl font-black tracking-widest hover:bg-[var(--gold-light)] transition-all shadow-[0_0_30px_var(--gold-glow)] active:scale-95 uppercase text-xs">
                    RETURN TO HOME
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FollowUp;
